import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tooltip,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { courseLinkageApi, linkageApi, scenarioStorageApi, contentApi, basePrefixApi } from '../services/api';
import { s3BucketsApi } from '../services/s3-buckets-api';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../services/rangeos-environment-api';

interface LinkageViewerProps {
  currentEnvironment: string;
}

interface CourseLinkageData {
  courseFilename: string;
  courseName?: string;
  currentVersion: string;
  availableVersions: { version: string; timestamp: string; isCurrent: boolean }[];
  activityLinkages: Record<string, string>;
  activities: ActivityLinkageData[];
}

interface ActivityLinkageData {
  activityFilename: string;
  activityName?: string;
  version: string;
  auLinkages: Record<string, string>;
  scenarios: ScenarioLinkageData[];
}

interface ScenarioLinkageData {
  auId: string;
  scenarioUuid: string;
  version: string;
  scenarioName?: string;
  exists: boolean;
  rangeosReady?: boolean;
}

const LinkageViewer: React.FC<LinkageViewerProps> = ({
  currentEnvironment,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [courseLinkages, setCourseLinkages] = useState<CourseLinkageData[]>([]);
  const [orphanedActivities, setOrphanedActivities] = useState<ActivityLinkageData[]>([]);
  const [bucketId, setBucketId] = useState<string>('');
  const [environmentId, setEnvironmentId] = useState<string>('');
  const [environmentPrefix, setEnvironmentPrefix] = useState<string>('');
  const [rangeosEnvironments, setRangeosEnvironments] = useState<RangeOSEnvironment[]>([]);
  const [auMappings, setAuMappings] = useState<Map<string, Map<string, { name: string; scenarios: string[]; scenarioNames: string[]; hasS3Linkage: boolean }>>>(new Map());
  const [scenarioNameCache, setScenarioNameCache] = useState<Map<string, string>>(new Map());
  const [loadingMappings, setLoadingMappings] = useState<Set<string>>(new Set());

  // Fetch environment details from backend's current environment
  // This ensures we use the exact environment selected in BasePrefixSelector
  useEffect(() => {
    const fetchEnvironmentDetails = async () => {
      if (!currentEnvironment) {
        setBucketId('');
        setEnvironmentId('');
        setEnvironmentPrefix('');
        return;
      }

      try {
        // Get the currently selected environment from the backend
        // This avoids ambiguity when multiple environments share the same prefix
        const env = await basePrefixApi.getCurrentEnvironment();
        console.log(`[LinkageViewer] Using backend's current environment: ${env.environmentName} (ID: ${env.environmentId}, prefix: ${env.prefix})`);
        setBucketId(env.bucketId);
        setEnvironmentId(env.environmentId);
        setEnvironmentPrefix(env.prefix);
      } catch (err) {
        console.warn('Failed to fetch current environment from backend:', err);
        setError('Failed to load environment details');
      }
    };

    fetchEnvironmentDetails();
  }, [currentEnvironment]);

  useEffect(() => {
    if (bucketId && environmentId) {
      loadAllLinkages();
      loadRangeOSEnvironments();
    }
  }, [bucketId, environmentId]);

  const loadRangeOSEnvironments = async () => {
    try {
      // Load all environments (not just active) so we can see all mappings
      // Inactive environments will be shown with a visual indicator
      const environments = await rangeosEnvironmentService.getEnvironments();
      setRangeosEnvironments(environments);
    } catch (err) {
      console.error('Failed to load RangeOS environments:', err);
    }
  };

  const loadAUMappingsForActivity = async (activity: ActivityLinkageData) => {
    if (rangeosEnvironments.length === 0) return;

    const activityKey = activity.activityFilename;

    // Set loading state
    setLoadingMappings(prev => new Set(prev).add(activityKey));

    try {
      // Get AU IDs from the CMI5 package to find all AUs with labs
      let auIdsWithLabs = new Set<string>();

    try {
      // Construct the full S3 key with versioning path
      const packageKey = `${environmentPrefix}/content/cmi5-activities/current/${activity.activityFilename}`;
      const config = await s3BucketsApi.getRC5Config(bucketId, environmentId, packageKey);

      if (config.aus || config.au) {
        const ausFromPackage = config.aus || config.au || [];
        console.log(`[LinkageViewer] Found ${ausFromPackage.length} AUs in package, sample:`, ausFromPackage[0]);

        // Filter AUs to only those that have moveOn: "Passed" (indicates a lab/assessment)
        for (const au of ausFromPackage) {
          if (au.id && au.id !== 'undefined' && au.id !== 'null') {
            // Check if this AU has a lab by looking for moveOn: "Passed"
            // If no moveOn property exists, include all AUs with valid IDs
            if (!au.moveOn || au.moveOn === 'Passed' || au.moveOn === 'CompletedAndPassed') {
              auIdsWithLabs.add(au.id);
              console.log(`[LinkageViewer] Including AU with lab: ${au.id} (moveOn: ${au.moveOn})`);
            }
          }
        }
      }
    } catch (err) {
      console.warn(`Failed to load AUs from package ${activity.activityFilename}:`, err);
    }

    // If we couldn't get AUs from package, fall back to S3 linkages
    if (auIdsWithLabs.size === 0 && activity.auLinkages) {
      Object.keys(activity.auLinkages)
        .filter(id => id && id !== 'undefined' && id !== 'null')
        .forEach(id => auIdsWithLabs.add(id));
    }

    const auIds = Array.from(auIdsWithLabs);
    if (auIds.length === 0) return;

    const newMappings = new Map<string, { name: string; scenarios: string[]; scenarioNames: string[]; hasS3Linkage: boolean }>();
    const scenarioUuidsToResolve = new Set<string>();

    // Track which AUs have S3 linkages
    const ausWithS3Linkage = new Set(activity.auLinkages ? Object.keys(activity.auLinkages) : []);

    // First pass: collect all mappings and scenario UUIDs
    for (const env of rangeosEnvironments) {
      try {
        const mappings = await rangeosEnvironmentService.getAUMappings(env.id, auIds);
        for (const mapping of mappings) {
          newMappings.set(`${env.id}:${mapping.auId}`, {
            name: mapping.name,
            scenarios: mapping.scenarios,
            scenarioNames: [],
            hasS3Linkage: ausWithS3Linkage.has(mapping.auId),
          });
          // Collect scenario UUIDs to resolve
          mapping.scenarios.forEach(uuid => scenarioUuidsToResolve.add(uuid));
        }
      } catch (err) {
        console.warn(`Failed to load AU mappings for environment ${env.id}:`, err);
      }
    }

    // Second pass: resolve scenario UUIDs to names
    const newScenarioNames = new Map<string, string>(scenarioNameCache);
    console.log(`[LinkageViewer] Resolving ${scenarioUuidsToResolve.size} scenario UUIDs to names...`);

    // Load scenario names from S3 only
    const s3ScenarioMap = new Map<string, string>();
    try {
      const scenarios = await scenarioStorageApi.listScenarios(bucketId, environmentId);
      console.log(`[LinkageViewer] Loaded ${scenarios.length} scenarios from S3`);
      for (const scenario of scenarios) {
        if (scenario.uuid && scenario.metadata?.scenarioInfo?.name) {
          s3ScenarioMap.set(scenario.uuid, scenario.metadata.scenarioInfo.name);
        }
      }
    } catch (err) {
      console.warn('Failed to load scenarios from S3:', err);
    }

    // Resolve each UUID from S3 or use shortened UUID
    for (const uuid of scenarioUuidsToResolve) {
      if (newScenarioNames.has(uuid)) continue; // Already in cache

      if (s3ScenarioMap.has(uuid)) {
        newScenarioNames.set(uuid, s3ScenarioMap.get(uuid)!);
      } else {
        // Just use shortened UUID if not in S3
        newScenarioNames.set(uuid, uuid.substring(0, 8) + '...');
      }
    }

    console.log('[LinkageViewer] Scenario resolution results:', {
      totalUuidsToResolve: scenarioUuidsToResolve.size,
      resolvedNamesCount: newScenarioNames.size,
      s3ScenarioCount: s3ScenarioMap.size,
      sampleResolutions: Array.from(newScenarioNames.entries()).slice(0, 5)
    });

    // Update scenario name cache
    setScenarioNameCache(newScenarioNames);

    // Third pass: populate scenario names in mappings
    for (const [key, mapping] of newMappings.entries()) {
      mapping.scenarioNames = mapping.scenarios.map(
        uuid => newScenarioNames.get(uuid) || uuid.substring(0, 8) + '...'
      );
    }

    console.log('[LinkageViewer] Final mappings with scenario names:',
      Array.from(newMappings.entries()).slice(0, 2).map(([key, val]) => ({
        key,
        scenarios: val.scenarios,
        scenarioNames: val.scenarioNames,
        hasS3Linkage: val.hasS3Linkage
      }))
    );

      // Force a new Map to trigger re-render
      const updatedMappings = new Map(auMappings);
      updatedMappings.set(activityKey, newMappings);
      setAuMappings(updatedMappings);
    } catch (error) {
      console.error('[LinkageViewer] Error loading AU mappings:', error);
    } finally {
      // Always clear loading state
      setLoadingMappings(prev => {
        const updated = new Set(prev);
        updated.delete(activityKey);
        return updated;
      });
    }
  };

  const loadAllLinkages = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get all courses in the environment (using version-aware listing)
      const courseFiles = await contentApi.getFolderContentsWithVersions('courses');
      const courseMBZFiles = courseFiles.filter(f => f.name.endsWith('.mbz'));

      // Get all CMI5 activities in the environment (using version-aware listing)
      const activityFiles = await contentApi.getFolderContentsWithVersions('cmi5_activities');
      const activityZipFiles = activityFiles.filter(f => f.name.endsWith('.zip'));

      const courseLinkagesData: CourseLinkageData[] = [];
      const activityFilenamesInCourses = new Set<string>();

      // Load linkages for each course
      for (const courseFile of courseMBZFiles) {
        const courseFilename = courseFile.name;

        try {
          const linkages = await courseLinkageApi.getCourseLinkages(bucketId, environmentId, courseFilename);

          if (Object.keys(linkages).length > 0) {
            const activities: ActivityLinkageData[] = [];

            for (const [activityFilename, version] of Object.entries(linkages)) {
              activityFilenamesInCourses.add(activityFilename);

              // Find activity file to get name from metadata (try different key formats)
              const activityFile = activityZipFiles.find(f => f.name === activityFilename);
              const activityName = activityFile?.metadata?.['Cmi5-activity-name']
                || activityFile?.metadata?.['cmi5-activity-name']
                || activityFile?.metadata?.['activity-name']
                || activityFile?.metadata?.['Activity Name']
                || activityFile?.metadata?.['activityname']
                || activityFile?.metadata?.['name']
                || activityFile?.metadata?.['title']
                || undefined;

              try {
                const auLinkages = await linkageApi.getLinkages(bucketId, environmentId, activityFilename);
                const scenarios: ScenarioLinkageData[] = [];

                for (const [auId, scenarioRef] of Object.entries(auLinkages)) {
                  // Skip undefined, null, or empty AU IDs
                  if (!auId || auId === 'undefined' || auId === 'null') {
                    console.warn(`Skipping invalid AU ID: ${auId}`);
                    continue;
                  }

                  const [scenarioUuid, scenarioVersion] = scenarioRef.split(':');

                  // Check if scenario exists
                  let scenarioName: string | undefined;
                  let exists = false;
                  try {
                    const scenarioList = await scenarioStorageApi.listScenarios(bucketId, environmentId);
                    const scenario = scenarioList.find(s => s.uuid === scenarioUuid);
                    if (scenario) {
                      exists = true;
                      scenarioName = scenario.metadata?.scenarioInfo?.name;
                    }
                  } catch (e) {
                    console.warn(`Failed to check scenario ${scenarioUuid}:`, e);
                  }

                  scenarios.push({
                    auId,
                    scenarioUuid,
                    version: scenarioVersion,
                    scenarioName,
                    exists,
                    rangeosReady: exists, // Assume if it exists in S3, it can be deployed to RangeOS
                  });
                }

                activities.push({
                  activityFilename,
                  activityName,
                  version,
                  auLinkages,
                  scenarios,
                });
              } catch (activityError) {
                console.warn(`Failed to load AU linkages for ${activityFilename}:`, activityError);
              }
            }

            // Get course version info from file metadata
            const courseVersion = courseFile.versionInfo?.currentVersion || 'v1';

            courseLinkagesData.push({
              courseFilename,
              courseName: courseFilename.replace('.mbz', ''),
              currentVersion: courseVersion,
              availableVersions: [], // Not needed anymore - versions managed in Courses tab
              activityLinkages: linkages,
              activities,
            });
          }
        } catch (courseError) {
          console.warn(`Failed to load linkages for ${courseFilename}:`, courseError);
        }
      }

      // Find orphaned activities (activities with linkages but not linked by any course)
      const orphanedActivitiesData: ActivityLinkageData[] = [];
      for (const activityFile of activityZipFiles) {
        const activityFilename = activityFile.name;
        const activityName = activityFile.metadata?.['Cmi5-activity-name']
          || activityFile.metadata?.['cmi5-activity-name']
          || activityFile.metadata?.['activity-name']
          || activityFile.metadata?.['Activity Name']
          || activityFile.metadata?.['activityname']
          || activityFile.metadata?.['name']
          || activityFile.metadata?.['title']
          || undefined;

        if (!activityFilenamesInCourses.has(activityFilename)) {
          try {
            const auLinkages = await linkageApi.getLinkages(bucketId, environmentId, activityFilename);

            if (Object.keys(auLinkages).length > 0) {
              const scenarios: ScenarioLinkageData[] = [];

              for (const [auId, scenarioRef] of Object.entries(auLinkages)) {
                const [scenarioUuid, scenarioVersion] = scenarioRef.split(':');

                let scenarioName: string | undefined;
                let exists = false;
                try {
                  const scenarioList = await scenarioStorageApi.listScenarios(bucketId, environmentId);
                  const scenario = scenarioList.find(s => s.uuid === scenarioUuid);
                  if (scenario) {
                    exists = true;
                    scenarioName = scenario.metadata?.scenarioInfo?.name;
                  }
                } catch (e) {
                  console.warn(`Failed to check scenario ${scenarioUuid}:`, e);
                }

                scenarios.push({
                  auId,
                  scenarioUuid,
                  version: scenarioVersion,
                  scenarioName,
                  exists,
                  rangeosReady: exists,
                });
              }

              orphanedActivitiesData.push({
                activityFilename,
                activityName,
                version: 'current',
                auLinkages,
                scenarios,
              });
            }
          } catch (e) {
            console.warn(`Failed to load linkages for orphaned activity ${activityFilename}:`, e);
          }
        }
      }

      setCourseLinkages(courseLinkagesData);
      setOrphanedActivities(orphanedActivitiesData);
    } catch (err) {
      console.error('Failed to load linkages:', err);
      setError(err instanceof Error ? err.message : 'Failed to load linkages');
    } finally {
      setLoading(false);
    }
  };

  const renderScenarioStatus = (scenario: ScenarioLinkageData) => {
    if (!scenario.exists) {
      return (
        <Tooltip title="Scenario not found in S3">
          <ErrorIcon color="error" fontSize="small" />
        </Tooltip>
      );
    }
    if (scenario.rangeosReady) {
      return (
        <Tooltip title="Ready to deploy to RangeOS">
          <CheckCircleIcon color="success" fontSize="small" />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Scenario exists but readiness unknown">
        <WarningIcon color="warning" fontSize="small" />
      </Tooltip>
    );
  };

  if (!currentEnvironment) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Alert severity="info">
          Please select an S3 environment from the dropdown in the top navigation bar.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Linkage Hierarchy Viewer
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadAllLinkages}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && courseLinkages.length === 0 && orphanedActivities.length === 0 && (
        <Alert severity="info">
          No linkages found. Linkages are created automatically when you export courses from Moodle to S3.
        </Alert>
      )}

      {/* Course Linkages */}
      {courseLinkages.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderIcon /> Courses ({courseLinkages.length})
          </Typography>

          {courseLinkages.map((course) => (
            <Accordion key={course.courseFilename} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                    {course.courseName || course.courseFilename}
                  </Typography>
                  <Chip
                    label={course.currentVersion}
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`${Object.keys(course.activityLinkages).length} activities`}
                    size="small"
                    color="primary"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {/* Course Version Info - Display Only */}
                  {course.currentVersion && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                        Course Version
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                          Current version: <strong>{course.currentVersion}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          (Manage versions in the Courses tab)
                        </Typography>
                      </Box>
                    </Paper>
                  )}

                  {course.activities.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No activity linkages found
                    </Typography>
                  )}

                  {course.activities.map((activity) => (
                    <Paper key={activity.activityFilename} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <DescriptionIcon color="primary" />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {activity.activityName || activity.activityFilename}
                          </Typography>
                          {activity.activityName && (
                            <Typography variant="caption" color="text.secondary">
                              {activity.activityFilename}
                            </Typography>
                          )}
                        </Box>
                        <Chip label={activity.version} size="small" variant="outlined" />
                      </Box>

                      {activity.scenarios.length > 0 && (
                        <Box sx={{ mt: 2, pl: 3, borderLeft: 2, borderColor: 'primary.main' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccountTreeIcon fontSize="small" /> AU → Scenario Linkages ({activity.scenarios.length})
                          </Typography>
                          <List dense>
                            {activity.scenarios.map((scenario, idx) => (
                              <ListItem key={idx} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        AU: <em>{scenario.auId.split('/').pop() || scenario.auId}</em>
                                      </Typography>
                                      <LinkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {scenario.scenarioName || scenario.scenarioUuid.substring(0, 8) + '...'}
                                      </Typography>
                                      <Chip label={scenario.version} size="small" variant="outlined" />
                                      {renderScenarioStatus(scenario)}
                                    </Box>
                                  }
                                  secondary={
                                    <Typography variant="caption" color="text.secondary">
                                      Scenario UUID: {scenario.scenarioUuid}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {activity.scenarios.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
                          No AU→scenario linkages
                        </Typography>
                      )}

                      {/* AU→RangeOS Environment Mapping Matrix */}
                      {rangeosEnvironments.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                              AU Mappings in RangeOS Environments
                            </Typography>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => loadAUMappingsForActivity(activity)}
                              disabled={loadingMappings.has(activity.activityFilename)}
                              sx={{ fontSize: '0.7rem', py: 0.25, px: 0.5 }}
                              startIcon={loadingMappings.has(activity.activityFilename) ? <CircularProgress size={12} /> : null}
                            >
                              {loadingMappings.has(activity.activityFilename) ? 'Loading...' : 'Load Mappings'}
                            </Button>
                          </Box>

                          {auMappings.has(activity.activityFilename) && (() => {
                            const activityMappings = auMappings.get(activity.activityFilename)!;
                            // Collect AU IDs from both S3 linkages and RangeOS mappings
                            // Filter out undefined, null, or empty AU IDs
                            const allAuIds = new Set<string>();

                            // Add AUs from S3 linkages
                            if (activity.auLinkages) {
                              Object.keys(activity.auLinkages)
                                .filter(id => id && id !== 'undefined' && id !== 'null')
                                .forEach(id => allAuIds.add(id));
                            }

                            // Add AUs from RangeOS mappings (these are AUs with labs)
                            activityMappings.forEach((_, key) => {
                              const colonIndex = key.indexOf(':');
                              if (colonIndex !== -1) {
                                const auId = key.substring(colonIndex + 1);
                                if (auId && auId !== 'undefined' && auId !== 'null') {
                                  allAuIds.add(auId);
                                }
                              }
                            });

                            console.log('[LinkageViewer] AU IDs for matrix (with labs):', Array.from(allAuIds));

                            return (
                              <Box sx={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ textAlign: 'left', padding: '4px', borderBottom: '1px solid #ddd' }}>AU</th>
                                      {rangeosEnvironments.map(env => (
                                        <th key={env.id} style={{ textAlign: 'center', padding: '4px', borderBottom: '1px solid #ddd' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                            {env.name}
                                            {env.status === 'inactive' && (
                                              <Tooltip title="Inactive environment">
                                                <Chip label="Inactive" size="small" color="default" sx={{ fontSize: '0.6rem', height: '16px' }} />
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Array.from(allAuIds).map((auId) => {
                                      const hasS3Linkage = activity.auLinkages && activity.auLinkages[auId];
                                      const hasAnyMapping = rangeosEnvironments.some(env =>
                                        activityMappings.has(`${env.id}:${auId}`)
                                      );

                                      return (
                                        <tr key={auId}>
                                          <td style={{ padding: '4px', borderBottom: '1px solid #f0f0f0' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <Tooltip title={auId}>
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                  {(() => {
                                                    // Show last meaningful part of URL, or full ID if short
                                                    if (auId.length < 30) return auId;
                                                    const parts = auId.split('/').filter(p => p && p !== 'https:' && p !== 'http:');
                                                    const lastPart = parts[parts.length - 1] || auId;
                                                    return lastPart.length > 25 ? lastPart.substring(0, 25) + '...' : lastPart;
                                                  })()}
                                                </Typography>
                                              </Tooltip>
                                              {!hasS3Linkage && hasAnyMapping && (
                                                <Tooltip title="Warning: AU has RangeOS mapping but no scenario linked in S3">
                                                  <WarningIcon sx={{ fontSize: '0.875rem', color: 'warning.main' }} />
                                                </Tooltip>
                                              )}
                                            </Box>
                                          </td>
                                          {rangeosEnvironments.map(env => {
                                            const mapping = activityMappings.get(`${env.id}:${auId}`);
                                            return (
                                              <td key={env.id} style={{ textAlign: 'center', padding: '4px', borderBottom: '1px solid #f0f0f0' }}>
                                                {mapping && mapping.scenarios.length > 0 ? (
                                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                                    {mapping.scenarios.map((uuid, idx) => {
                                                      const scenarioName = mapping.scenarioNames?.[idx] || uuid.substring(0, 8) + '...';
                                                      // Construct dashboard URL from devOpsApiUrl
                                                      // e.g., https://rangeos-api.develop-cp.rangeos.engineering -> https://rangeos-dashboard.develop-cp.rangeos.engineering
                                                      const dashboardUrl = env.devOpsApiUrl.replace('rangeos-api', 'rangeos-dashboard');
                                                      const scenarioUrl = `${dashboardUrl}/components/scenarios/${uuid}`;

                                                      return (
                                                        <Tooltip
                                                          key={uuid}
                                                          title={
                                                            <Box>
                                                              <Typography variant="caption" display="block">
                                                                {mapping.scenarioNames?.[idx] ? `Name: ${mapping.scenarioNames[idx]}` : 'Name not available'}
                                                              </Typography>
                                                              <Typography variant="caption" display="block">
                                                                UUID: {uuid}
                                                              </Typography>
                                                              {!mapping.hasS3Linkage && (
                                                                <Typography variant="caption" display="block" color="warning.main">
                                                                  ⚠️ No S3 scenario linkage
                                                                </Typography>
                                                              )}
                                                            </Box>
                                                          }
                                                        >
                                                          <Chip
                                                            label={scenarioName}
                                                            size="small"
                                                            color={mapping.hasS3Linkage ? "success" : "warning"}
                                                            variant="outlined"
                                                            component="a"
                                                            href={scenarioUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            clickable
                                                            sx={{
                                                              fontSize: '0.65rem',
                                                              height: '20px',
                                                              cursor: 'pointer',
                                                              textDecoration: 'none',
                                                              '&:hover': {
                                                                backgroundColor: mapping.hasS3Linkage ? 'success.light' : 'warning.light'
                                                              }
                                                            }}
                                                          />
                                                        </Tooltip>
                                                      );
                                                    })}
                                                  </Box>
                                                ) : (
                                                  <Typography variant="caption" color="text.disabled">
                                                    -
                                                  </Typography>
                                                )}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </Box>
                          );
                          })()}
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Orphaned Activities */}
      {orphanedActivities.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" /> Orphaned Activities ({orphanedActivities.length})
            <Tooltip title="Activities with AU linkages but not linked by any course">
              <WarningIcon fontSize="small" />
            </Tooltip>
          </Typography>

          {orphanedActivities.map((activity) => (
            <Paper key={activity.activityFilename} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'warning.light', opacity: 0.7 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DescriptionIcon color="warning" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {activity.activityName || activity.activityFilename}
                  </Typography>
                  {activity.activityName && (
                    <Typography variant="caption" color="text.secondary">
                      {activity.activityFilename}
                    </Typography>
                  )}
                </Box>
                <Chip label="No course link" size="small" color="warning" />
              </Box>

              {activity.scenarios.length > 0 && (
                <Box sx={{ mt: 2, pl: 3, borderLeft: 2, borderColor: 'warning.main' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccountTreeIcon fontSize="small" /> AU → Scenario Linkages ({activity.scenarios.length})
                  </Typography>
                  <List dense>
                    {activity.scenarios.map((scenario, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                AU: <em>{scenario.auId.split('/').pop() || scenario.auId}</em>
                              </Typography>
                              <LinkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {scenario.scenarioName || scenario.scenarioUuid.substring(0, 8) + '...'}
                              </Typography>
                              <Chip label={scenario.version} size="small" variant="outlined" />
                              {renderScenarioStatus(scenario)}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LinkageViewer;
